import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["street", "city", "state", "postalCode", "dropdown"]
  static values = {
    radarKey: String,
    states: Array
  }

  connect() {
    this.searchTimeout = null
  }

  disconnect() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
  }

  onInput(event) {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }

    const query = event.target.value.trim()

    if (query.length < 3) {
      this.hideDropdown()
      return
    }

    // Debounce the API call
    this.searchTimeout = setTimeout(() => {
      this.searchAddresses(query)
    }, 300)
  }

  async searchAddresses(query) {
    try {
      const response = await fetch(`https://api.radar.io/v1/search/autocomplete?query=${encodeURIComponent(query)}&country=US&limit=5`, {
        headers: {
          'Authorization': this.radarKeyValue
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()

      if (data?.addresses?.length > 0) {
        this.displaySuggestions(data.addresses)
      } else {
        this.hideDropdown()
      }
    } catch (error) {
      console.error('Radar API error:', error)
      this.hideDropdown()
    }
  }

  displaySuggestions(addresses) {
    this.dropdownTarget.innerHTML = ''

    addresses.forEach((address, index) => {
      const item = document.createElement('div')
      item.className = 'px-4 py-3 cursor-pointer text-sm hover:bg-base-200 transition-colors'

      if (index < addresses.length - 1) {
        item.className += ' border-b border-base-200'
      }

      const addressLine = document.createElement('div')
      addressLine.className = 'font-medium'
      addressLine.textContent = this.formatStreetAddress(address)

      const cityLine = document.createElement('div')
      cityLine.className = 'text-xs text-base-content/60 mt-0.5'
      cityLine.textContent = `${address.city || ''}, ${address.state || ''} ${address.postalCode || ''}`.trim()

      item.appendChild(addressLine)
      item.appendChild(cityLine)

      item.dataset.action = 'click->address-autocomplete#selectAddress'
      item.dataset.address = JSON.stringify(address)

      this.dropdownTarget.appendChild(item)
    })

    this.dropdownTarget.classList.remove('hidden')
  }

  formatStreetAddress(address) {
    if (address.addressLabel) {
      return address.addressLabel
    }

    const parts = []
    if (address.number) parts.push(address.number)
    if (address.street) parts.push(address.street)

    return parts.join(' ') || 'Unknown address'
  }

  selectAddress(event) {
    const address = JSON.parse(event.currentTarget.dataset.address)

    if (this.hasStreetTarget) {
      this.streetTarget.value = address.addressLabel || ''
    }

    if (this.hasCityTarget) {
      this.cityTarget.value = address.city || ''
    }

    if (this.hasPostalCodeTarget) {
      this.postalCodeTarget.value = address.postalCode || ''
    }

    // Find and select/set the state
    if (this.hasStateTarget) {
      const stateAbbr = address.stateCode || address.state

      if (stateAbbr) {
        // If we have a states array, use it for select-based fields
        if (this.statesValue && this.statesValue.length > 0) {
          // Try to match by abbreviation first
          let state = this.statesValue.find(s =>
            s.abbr.toUpperCase() === stateAbbr.toUpperCase()
          )

          // If no match, try matching by full name
          if (!state && address.state) {
            state = this.statesValue.find(s =>
              s.name.toLowerCase() === address.state.toLowerCase()
            )
          }

          if (state) {
            this.stateTarget.value = state.id
            this.stateTarget.dispatchEvent(new Event('change'))
          }
        } else {
          // For text-based state fields, just set the abbreviation
          this.stateTarget.value = stateAbbr
        }
      }
    }

    this.hideDropdown()
  }

  hideDropdown() {
    if (this.hasDropdownTarget) {
      this.dropdownTarget.classList.add('hidden')
    }
  }

  clickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.hideDropdown()
    }
  }
}
